# Route: /admin/roles/create
# Create a new role: name, description, and a permission picker.
# Permission: Role:create (enforced by POST /api/users/roles; the list-page
# affordance that routes here is gated on the same permission).
#
# This form carries no parent-role field: a role is created without a parent,
# and its edit page (role-edit.cir.yaml) is where "Inherits from" is shown and
# set. POST /api/users/roles accepts `inherits` for a caller of the API.
#
# Canonical type: Form + FormActions pattern (same as tenant-create). The flat
# fields (name, description) are collected by the Form's emitted
# _collectFormData (model-resolved bind paths, shadow-DOM aware); the nested
# `permissions` array is injected onto the create action by the
# xp87-role-permission-picker widget's capture-phase interceptor.

page-admin-role-create:
  type: Page
  archetype: create
  meta:
    title: $t.admin.roles.create_title
    auth: required
  data:
    form:
      model: CreateRoleForm
      initial:
        name: ""
        description: ""
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - role-create-header
    - role-create-form

# ── Header ───────────────────────────────────────────────────────────────────

role-create-header:
  type: PageHeader
  title: $t.admin.roles.create_title
  back_route: /admin/roles

# ── Form ─────────────────────────────────────────────────────────────────────

role-create-form:
  type: Form
  model: CreateRoleForm
  a11y:
    label: $t.admin.roles.create_title
  action:
    type: create
    model: RoleDto
    on_success: navigate(/admin/roles)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - role-create-card
    - role-create-permissions
    - role-create-actions

role-create-card:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - role-create-field-name
    - role-create-field-description

role-create-field-name:
  type: Input
  input_type: text
  label: $t.admin.roles.field_name
  bind:
    content: form.name
  a11y:
    label: $t.admin.roles.field_name_label
  validation:
    required: true
    messages:
      invalid: $t.admin.roles.field_name_invalid
  spatial:
    min_height: $size.touch-target

role-create-field-description:
  type: Input
  input_type: textarea
  label: $t.admin.roles.field_description
  bind:
    content: form.description
  a11y:
    label: $t.admin.roles.field_description_label
  spatial:
    min_height: $size.touch-target

# ── Permission picker (custom widget — interim for xp85 REQ-032) ──

role-create-permissions:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - role-create-picker

role-create-picker:
  type: Widget
  ref: xp87-role-permission-picker

# ── Submit / Cancel ──────────────────────────────────────────────────────────

role-create-actions:
  type: FormActions
  submit_label: $t.admin.roles.create_submit
  cancel_route: /admin/roles
